import { type LicenseLogo } from "./getLicenseLogos"
import { type LogoVariant } from "./licenseLogos"

export type LayoutCell = {
  row: number
  col: number
  logos: LicenseLogo[]
  isGroup?: boolean
}

export type LayoutResult = {
  cells: LayoutCell[]
  columns: number
  rows: number
  sharingWidth: number
}

export const ICON_SIZE = 56
export const GAP = 0

const sharingOrder: Array<LicenseLogo["key"]> = [
  "sharing-direct",
  "sharing-collaborative",
  "sharing-federated",
  "sharing-tre"
]

type AxisKey =
  | "commercial"
  | "onward"
  | "benefit"
  | "cost"
  | "publishing"
  | "attribution"

const axisOrder: Record<AxisKey, number> = {
  commercial: 0,
  onward: 1,
  benefit: 2,
  cost: 3,
  publishing: 4,
  attribution: 5
}

const statusOrder: Record<"allowed" | "bonus" | "restricted", number> = {
  allowed: 0,
  bonus: 1,
  restricted: 2
}

export const resolveLogoVariant = (logo: LicenseLogo): LogoVariant =>
  sharingOrder.includes(logo.key) ? "bw" : logo.variant

export const buildLogoBarLayout = (logos: LicenseLogo[]): LayoutResult => {
  const byKey = new Map(logos.map(logo => [logo.key, logo]))
  const cells: LayoutCell[] = []
  const used = new Set<LicenseLogo["key"]>()
  const suppressed = new Set<LicenseLogo["key"]>()

  let maxCol = -1
  let hasSecondRow = false

  const addCell = (cell: LayoutCell) => {
    if (!cell.logos.length) return
    cells.push(cell)
    cell.logos.forEach(logo => used.add(logo.key))
    maxCol = Math.max(maxCol, cell.col)
    if (cell.row > 0) hasSecondRow = true
  }

  const getLogo = (key: LicenseLogo["key"]) => byKey.get(key)
  const firstLogo = (keys: LicenseLogo["key"][]): LicenseLogo | undefined =>
    keys.map(getLogo).find(Boolean)

  // Sharing logos take the first two columns and flow to a second row if needed.
  const sharingLogos = sharingOrder.map(getLogo).filter(Boolean) as LicenseLogo[]
  const sharingWidth =
    sharingLogos.length === 0 ? 0 : sharingLogos.length === 1 ? 1 : 2

  sharingLogos.forEach((logo, index) => {
    addCell({
      row: Math.floor(index / 2),
      col: index % 2,
      logos: [logo]
    })
  })

  let currentCol = sharingWidth
  const axisColumn: Partial<Record<AxisKey, number>> = {}

  type AxisEntry = {
    axis: AxisKey
    logo: LicenseLogo
    status: keyof typeof statusOrder
    hasSubitems: boolean
  }

  const axisEntries: AxisEntry[] = []

  const sharingAllowed = Boolean(getLogo("sharing-onward"))
  const onwardGroup = [
    getLogo("share-alike"),
    getLogo("modifications-no")
  ].filter(Boolean) as LicenseLogo[]

  const publishingGroup = [
    getLogo("publication-embargo"),
    getLogo("publication-review"),
    getLogo("aggregated-data-reporting")
  ].filter(Boolean) as LicenseLogo[]

  const attributionOutputs = getLogo("attribution-outputs")
  const attributionAuthor = getLogo("attribution-author")
  const attributionGroup = [attributionOutputs, attributionAuthor].filter(
    Boolean
  ) as LicenseLogo[]

  const axisSubitems: Partial<Record<AxisKey, boolean>> = {
    onward: sharingAllowed && onwardGroup.length > 0,
    publishing: publishingGroup.length > 0,
    attribution: attributionGroup.length > 0
  }

  if (!sharingAllowed) {
    onwardGroup.forEach(logo => suppressed.add(logo.key))
  }

  const addAxis = (
    axis: AxisKey,
    keys: LicenseLogo["key"][],
    statusForKey: (key: LicenseLogo["key"]) => AxisEntry["status"]
  ) => {
    const logo = firstLogo(keys)
    if (!logo) return
    axisEntries.push({
      axis,
      logo,
      status: statusForKey(logo.key),
      hasSubitems: Boolean(axisSubitems[axis])
    })
  }

  addAxis("commercial", ["commercial-yes", "commercial-no"], key =>
    key === "commercial-yes" ? "allowed" : "restricted"
  )

  addAxis("onward", ["sharing-onward", "sharing-no-onward"], key =>
    key === "sharing-onward" ? "allowed" : "restricted"
  )

  addAxis("benefit", ["benefit-sharing"], () => "bonus")
  addAxis("cost", ["cost-recovery"], () => "bonus")

  addAxis("publishing", ["publication-yes", "publication-no"], key =>
    key === "publication-yes" ? "allowed" : "restricted"
  )

  addAxis(
    "attribution",
    ["attribution-no", "attribution-yes"],
    key => (key === "attribution-no" ? "allowed" : "restricted")
  )

  axisEntries.sort((a, b) => {
    const statusDiff = statusOrder[a.status] - statusOrder[b.status]
    if (statusDiff !== 0) return statusDiff
    if (a.hasSubitems !== b.hasSubitems) return a.hasSubitems ? -1 : 1
    return axisOrder[a.axis] - axisOrder[b.axis]
  })

  axisEntries.forEach(entry => {
    const col = currentCol++
    axisColumn[entry.axis] = col
    addCell({
      row: 0,
      col,
      logos: [entry.logo]
    })
  })

  if (sharingAllowed && axisColumn.onward !== undefined && onwardGroup.length) {
    addCell({
      row: 1,
      col: axisColumn.onward,
      logos: onwardGroup,
      isGroup: true
    })
  }

  if (axisColumn.publishing !== undefined && publishingGroup.length) {
    addCell({
      row: 1,
      col: axisColumn.publishing,
      logos: publishingGroup,
      isGroup: true
    })
  }

  if (axisColumn.attribution !== undefined && attributionGroup.length) {
    addCell({
      row: 1,
      col: axisColumn.attribution,
      logos: attributionGroup,
      isGroup: true
    })
  }

  const leftover = logos.filter(
    logo => !used.has(logo.key) && !suppressed.has(logo.key)
  )
  leftover.forEach((logo, index) => {
    addCell({
      row: hasSecondRow ? 1 : 0,
      col: currentCol + index,
      logos: [logo]
    })
  })

  const columns = Math.max(maxCol + 1, 0)
  const rows = hasSecondRow ? 2 : 1

  return { cells, columns, rows, sharingWidth }
}
